import { Injectable } from '@angular/core';
import { Observable, Subject } from "rxjs";
import { catchError } from 'rxjs/operators';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import * as moment from 'moment';
import { fuseConfig } from '../../fuse-config';
import * as faye from 'faye';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { Router } from '@angular/router';
import { CdnPhotoService } from 'app/shared/cdn-photo.service';

@Injectable({
    providedIn: 'root'
})
export class SecurityService {
    private subject;
    private scannerSubject;
    private monitorSubject;
    public selectedGate;
    private _selectedBuilding;
    public selectedBuildingSettings;
    public selectedGateCameras;
    public selectedGateDoor;
    public hasScanner;
    public currentEvent;
    public saveShowLicenseNumber;
    public gate = null;
    public isCheckedIn: boolean;
    public selectedGateObject;

    constructor(
        private http: HttpClient, private router: Router,
        private _fuseNavigationService: FuseNavigationService,
        private cdnPhotoService: CdnPhotoService,
    ) {
        this.subject = new Subject();
        this.scannerSubject = new Subject();
        this.monitorSubject = new Subject();
    }

    monitorSubscribe(next, error?, complete?): any {
        return this.monitorSubject.subscribe(next, error, complete);
    }

    monitorNext(data): any {
        return this.monitorSubject.next(data);
    }

    monitorUnsubscribe(): any {
        return this.monitorSubject.unsubscribe();
    }

    getSelectedGateObject(): any {
        return this.selectedGateObject;
    }


    scannerSubscribe(next, error?, complete?): any {
        return this.scannerSubject.subscribe(next, error, complete);
    }

    scannerUnsubscribe(): any {
        return this.scannerSubject.unsubscribe();
    }

    scan(data): any {
        this.scannerSubject.next(data);
    }

    subscribe(next, error?, complete?): any {
        return this.subject.subscribe(next, error, complete);
    }

    unsubscribe(): any {
        return this.subject.unsubscribe();
    }

    selectGate(gate): any {
        this.gate = gate;
        this.subject.next(gate);
    }

    getGates(): Observable<any> {
        let gate_type = 0
        const access_gate_type = sessionStorage.getItem('access_gate_type')
        if (parseInt(access_gate_type) === 99) {
            gate_type = 99
        }
        if (parseInt(access_gate_type) === 1) {
            gate_type = 1
        }
        if (parseInt(access_gate_type) === 2) {
            gate_type = 2
        }
        if (this.router.url.includes('security/desk-attendant')) {
            gate_type = 2
        }
        if (this.router.url.includes('security/control')) {
            gate_type = 1
        }
        const gateFilter = gate_type === 0 ? '' : gate_type === 99 ? '/?gate_type=' + 'All' : '/?gate_type=' + gate_type

        return this.http.get(fuseConfig.url + '/security/gates' + gateFilter);
    }

    getDeskAttendant(id: string, datetimeFrom: string, datetimeTo: string, guestTypes: string[], guestName?: string): Observable<any> {

        let params = new HttpParams().set('guestType', JSON.stringify(guestTypes));
        if (datetimeFrom) {
            params = params.set('dateFrom', datetimeFrom);
        }
        if (datetimeTo) {
            params = params.set('dateTo', datetimeTo);
        }

        return this.http.get(fuseConfig.url + '/security/getDeskAttendant/' + id + (guestName !== '' ? `?guestName=${guestName}` : ''),{ params: params });
    }

    scanQR(body): Observable<any> {
        return this.http.post(fuseConfig.url + '/security/scan', body);
    }

    getLastGuest(): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/lastGuest');
    }

    getTodayGuests(gate): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/todayGuests/' + gate);
    }

    getBuildingUnits(building): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getBuildingUnits/' + building);
    }

    getUnitHosts(building, unit): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getUnitHosts/' + building + '/' + unit);
    }

    getUnitScans(unit): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getUnitScans/' + unit);
    }

    getUnitNotifications(unit): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getUnitNotifications/' + unit);
    }

    createInvitation(model): Observable<any> {
        const body = { ...model };
        if (body.allDay === true) {
            delete body.fromHour;
            delete body.toHour;
        }
        body.fromDate = moment(body.fromDate).format('YYYY-MM-DD');
        body.toDate = moment(body.toDate).format('YYYY-MM-DD');
        return this.http.post(fuseConfig.url + '/security/quickInvitation', body);
    }

    getOwnerGuests(id): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getOwnerGuests/' + id);
    }

    /**
     * Compliance Module: contexto del carnet del guest antes de admitirlo.
     * Devuelve { applies: false } si el guest no requiere carnet (creado por
     * el guardia, tipo no configurado o modulo apagado).
     */
    getComplianceContext(userGuest): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/compliance/context/' + userGuest);
    }

    searchOwners(owner, building): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getQuickList/' + building + `?ownername=${owner}`);
    }

    /**
     * 🔴 RPGS · E6 · NO BORRAR.
     * `?pin=` -> listType 'pin' -> security/get-quick-list-pin.sql, que filtra por
     * `unit_house.pin`: el PIN VIEJO de la unidad, el que no abre nada.
     * Es EL ROLLBACK de E6: devolver `PINCODE_SEARCH_MODE` a 'pin' en
     * control.component.ts restaura el comportamiento anterior sin tocar la API.
     * Vive hasta E10.
     */
    searchPin(pin, building): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getQuickList/' + building + `?pin=${pin}`);
    }

    /**
     * RPGS · E6 · Busqueda del guardia por RESIDENT PINCODE.
     * body `residentpin` -> listType 'rpin' -> security/get-quick-list-resident-pin.sql,
     * que filtra por `resident.pin`: el pincode VIGENTE, el mismo que validan el
     * kiosk y el lector QR.
     *
     * 🔴 Va por POST con el PIN en el BODY, nunca en la URL. Este es un PIN que
     * abre puertas: como `?residentpin=` quedaba escrito en el access log de nginx
     * y en los breadcrumbs y spans de Sentry (que registran la URL, no el body).
     * El GET con `?residentpin=` ya responde 400. No volver a armar una URL con el
     * PIN ni pasarlo a un console.log (Sentry tambien guarda la consola).
     *
     * El SQL es un fork de get-quick-list-pin.sql (R6) y conserva tal cual el scope
     * master-asociado del camino del guardia (decision 15): el guardia es el
     * OVERRIDE HUMANO y no adopta el modelo por gate. La respuesta tiene la MISMA
     * forma que la de `searchPin` (unitaddress / unit / hostname / resident / ...),
     * asi que el consumidor es identico en ambos carriles.
     */
    searchResidentPin(pin, building): Observable<any> {
        return this.http.post(fuseConfig.url + '/security/getQuickList/' + building, { residentpin: pin });
    }

    searchUnits(unit, building): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getQuickList/' + building + `?address=${unit}`);
    }

    searchGuests(guest, building): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getQuickList/' + building + `?guestname=${guest}&carplate=${guest}`);
    }

    searchPhones(phone, building): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getQuickList/' + building + `?ownerphone=${phone}`);
    }

    // Con el CDN disponible las fotos no se piden en base64: cada residente
    // trae en `img_url` el edificio y el identificador (ver getResidentImage).
    getUnitResidents(unit): Observable<any> {
        const excludeImg = this.cdnPhotoService.isReady(this.selectedBuilding) ? '?excludeImg=true' : '';
        return this.http.get(fuseConfig.url + '/security/getUnitResidents/' + unit + excludeImg);
    }

    getUnitGuests(unit, filterActive): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getUnitGuests/' + unit + `?filterActive=${filterActive}`);
    }

    getUnitGuestsByName(unit, filterActive, name): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getUnitGuests/' + unit + `?filterActive=${filterActive}&filter=guestname:like:${name}`);
    }

    getUnitGuestsByCompany(unit, filterActive, company): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getUnitGuests/' + unit + `?filterActive=${filterActive}&filter=companyName:like:${company}`);
    }

    getUnitVehicles(unit): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getUnitVehicles/' + unit);
    }

    getResidentVehicles(unit, resident): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getUnitVehicles/' + unit + `?filter=resident:eq:${resident}`);
    }

    getResidentGuests(unit, resident, filterActive): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getUnitGuests/' + unit + `?filterActive=${filterActive}` + `&filter=resident:eq:${resident}`);
    }

    // Un API que no conoce `excludeImg` lo ignora y sigue mandando el base64.
    getOwnerDetails(unit): Observable<any> {
        const excludeImg = this.cdnPhotoService.isReady(this.selectedBuilding) ? '?excludeImg=true' : '';
        return this.http.get(fuseConfig.url + '/security/getOwnerDetails/' + unit + excludeImg);
    }

    sendNotification(unit, message): Observable<any> {
        const body = {
            unitHouse: unit,
            message: message
        };
        return this.http.post(fuseConfig.url + '/security/postUnitNotification', body);
    }

    getDoors(connector): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/goutru_connector/' + connector + '/door');
    }

    getGateDoors(gate): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/gateDoors/' + gate);
    }

    openDoor(door): Observable<any> {
        return this.http.post(fuseConfig.url + '/security/door/open', { door });
    }

    // getPhoto(gtc_port, id, channel): Observable<any> {
    //     const token = localStorage.getItem('token') ? localStorage.getItem('token') : sessionStorage.getItem('token');
    //     const headers = new HttpHeaders().set('authorization', token);
    //     headers.append('Content-Type', 'application/json');
    //     return this.http.get( fuseConfig.url2 + `/device/${id}/photo/${channel}/${gtc_port}`, {headers: headers, withCredentials: true});
    // }

    getPhoto(device): Observable<any> {
        return this.http.post(fuseConfig.url + '/security/device/photo/', { device: device });
    }

    scanLicense(): Observable<any> {
        return this.http.get(fuseConfig.scannerUrl + '/scan/');
    }

    getInvitationDetails(gate, id , invitation_type = null): Observable<any> {
        if (invitation_type) {
            return this.http.get(fuseConfig.url + '/security/getInvitationDetails/' + gate + '/' + id + `?invitation_type=${invitation_type}`);            
        }else{
            return this.http.get(fuseConfig.url + '/security/getInvitationDetails/' + gate + '/' + id);
        }
    }

    getGroupInvitationDetails(id): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getGroupInvitationDetails/' + id);
    }

    getScanImage(camera, scanId, type): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/getScanImage/' + scanId + '/' + type + '/' + camera);
    }

    scanGroupInvitation(data): Observable<any> {
        return this.http.post(fuseConfig.url + '/security/scanGroupInvitation/', data);
    }

    scanResident(data): Observable<any> {
        return this.http.post(fuseConfig.url + '/security/scanResident/', data);
    }

    getLogAccees(): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/monitor/doorEvents');
    }

    getLastAcceesEvent(building, gate): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/monitor/lastEventEntry/' + building + '/' + gate);
    }

    getPhotoEvent(gtc_port, eventId): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/monitor/photoEvent/' + gtc_port + '/' + eventId);
    }

    setupMonitorLiveWS(): any {
        const token = localStorage.getItem('token') ? localStorage.getItem('token') : sessionStorage.getItem('token');

        const wsClient = new faye.Client(fuseConfig.fayeUrl + '/fayeWs', {
            retry: 30
        }); // WS URL DE SERVER : port / lib

        wsClient.addExtension({
            outgoing: (message: any, callback: any) => { // function(message : any, callback : any) {
                message.ext = message.ext || {};
                message.ext.token = token;
                callback(message);
            }
        });

        return wsClient;
    }

    // Sin parametros: el catalogo completo (para mostrar el tipo de invitados
    // existentes). Con `building`: solo los tipos activos del sitio, y con
    // `availableNow` ademas solo los disponibles a la hora local del sitio. Es
    // filtro visual de la Quick Invitation: el API no valida nada con esto.
    getGuestTypes(building?: string | null, availableNow = false): Observable<any> {
        let params = '';
        if (building) {
            params = '?building=' + building + (availableNow ? '&available_now=true' : '');
        }
        return this.http.get(fuseConfig.url + '/shared/guest_type' + params);
    }

    getGuestGroupTypes(): Observable<any> {
        return this.http.get(fuseConfig.url + '/shared/guest_type?filter=for_groups:eq:true');
    }

    getEvents(form, text): Observable<any> {
        const headers = new HttpHeaders().set('Login-Origin', 'web');
        const filter = { ...form };
        let search = '';
        let slice = false;
        if (filter.dateFrom !== '' || filter.dateTo !== '') {
            search = `?dateFrom=${moment(filter.dateFrom).format('YYYY-MM-DD')}&dateTo=${moment(filter.dateTo).format('YYYY-MM-DD')}`;
        }

        if (filter.status !== '') {
            search.length > 0 ? search += '&status=' : search = '?filter=';
            search += filter.status;
        }
        if (filter.type !== 'X' || filter.unit !== '' || (text ? text !== '' : filter.guestName !== '')) {
            search.length > 0 ? search += '&filter=' : search = '?filter=';
            slice = true;
        }
        if (filter.type !== 'X') {
            search += `type:eq:${filter.type}|`;
        }
        if (filter.unit !== '') {
            search += `unit:eq:${filter.unit}|`;
        }
        // if (filter.dateFrom !== '') {
        //     search += `dateFromBegin:gte:${moment(filter.dateFrom).format('YYYY-MM-DD')}|`;
        // }
        // if (filter.dateTo !== '') {
        //     search += `dateFromEnd:lte:${moment(filter.dateTo).format('YYYY-MM-DD')}|`;
        // }
        // if (filter.guestName !== '') {
        //     search += `guestName:rlike:${filter.guestName}|`;
        // }
        if ((text ? text !== '' : filter.guestName)) {
            search += `guestName:like:%` + (text ? text : filter.guestName) + '%|';
        }

        if (filter.building !== '') {
            search += `building:eq:${filter.building}|`
        }
        // if (filter.dateFrom !== '') {
        //     search += `date:eq:${moment(filter.dateFrom).format('YYYY-MM-DD')}|`
        // }

        if (slice === true) {
            search = search.slice(0, -1);
        }

        return this.http.get(fuseConfig.url + '/security/events' + search, { headers: headers });
    }

    getEventCounter(form, text): Observable<any> {
        const headers = new HttpHeaders().set('Login-Origin', 'web');
        const filter = { ...form };
        let search = '';
        let slice = false;
        if (filter.dateFrom !== '' || filter.dateTo !== '') {
            search = `?dateFrom=${moment(filter.dateFrom).format('YYYY-MM-DD')}&dateTo=${moment(filter.dateTo).format('YYYY-MM-DD')}`;
        }

        if (filter.status !== '') {
            search.length > 0 ? search += '&status=' : search = '?filter=';
            search += filter.status;
        }
        if (filter.type !== 'X' || filter.unit !== '' || (text ? text !== '' : filter.guestName !== '')) {
            search.length > 0 ? search += '&filter=' : search = '?filter=';
            slice = true;
        }
        if (filter.type !== 'X') {
            search += `type:eq:${filter.type}|`;
        }
        if (filter.unit !== '') {
            search += `unit:eq:${filter.unit}|`;
        }
        // if (filter.dateFrom !== '') {
        //     search += `dateFromBegin:gte:${moment(filter.dateFrom).format('YYYY-MM-DD')}|`;
        // }
        // if (filter.dateTo !== '') {
        //     search += `dateFromEnd:lte:${moment(filter.dateTo).format('YYYY-MM-DD')}|`;
        // }
        // if (filter.guestName !== '') {
        //     search += `guestName:rlike:${filter.guestName}|`;
        // }
        if ((text ? text !== '' : filter.guestName)) {
            search += `guestName:like:%` + (text ? text : filter.guestName) + '%|';
        }

        if (filter.building !== '') {
            search += `building:eq:${filter.building}|`
        }
        // if (filter.dateFrom !== '') {
        //     search += `date:eq:${moment(filter.dateFrom).format('YYYY-MM-DD')}|`
        // }

        if (slice === true) {
            search = search.slice(0, -1);
        }

        return this.http.get(fuseConfig.url + '/security/events/counter' + search, { headers: headers });
    }

    getGroupInvitationGuests(id): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/events/' + id + '/guests');
    }

    getGuestLicensePhoto(url): Observable<any> {
        return this.http.get(fuseConfig.url + url);
    }

    getNotificationRules(unit, removeCode): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/notification_rules/' + unit + `?removeCode=${removeCode}`);
    }

    getResidentQRDetails(building, qr): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/residentQR/' + building + '/' + qr);
    }

    /**
     * Consulta un access code SIN consumirlo.
     *
     * El endpoint que usa el kiosko valida y consume en un mismo paso, así que
     * no sirve acá: el guardia necesita ver el estado del código —y el motivo
     * si no puede pasar— antes de decidir, y consultarlo no puede gastarlo.
     */
    getAccessCodeLookup(building, code): Observable<any> {
        return this.http.get(fuseConfig.url + '/security/accessCodes/lookup/' + building + '/' + encodeURIComponent(code));
    }

    /**
     * Registra la entrada, admitida o rechazada.
     *
     * No pasa por quickInvitation: ahí el guardia crea la invitación al vuelo
     * porque el visitante no existe todavía. Un access code ya lo creó el
     * residente, así que sólo se registra el uso.
     *
     * El API puede responder admitted=false aunque se haya admitido: entre la
     * consulta y este llamado el código pudo vencerse o gastarse en otra puerta.
     */
    scanAccessCode(model): Observable<any> {
        return this.http.post(fuseConfig.url + '/security/accessCodes/scan', model);
    }

    getVehicleReport(building, filter): Observable<any> {

        let filterValues = "?filter=";
        let filtersAdded = false;

        if (filter.unit) {
            filterValues += `unit:like:${filter.unit}`;
            filtersAdded = true;
        }

        if (filter.guest) {
            filterValues += (filtersAdded ? "|" : "") + `guest:like:${filter.guest}`;
            filtersAdded = true;
        }

        if (filter.plate) {
            filterValues += (filtersAdded ? "|" : "") + `plate:like:${filter.plate}`;
            filtersAdded = true;
        }

        // if (filter.tag) {
        //     filterValues += `|tag:like:${filter.tag}`;
        // }

        if (filter.cardCodeFrom && filter.cardCodeTo) {
            filterValues += (filtersAdded ? "|" : "") + `cardCode:r:${filter.cardCodeFrom}:${filter.cardCodeTo}`;
            filtersAdded = true;
        } else if (filter.cardCodeFrom) {
            filterValues += (filtersAdded ? "|" : "") + `cardCode:gte:${filter.cardCodeFrom}`;
            filtersAdded = true;
        } else if (filter.cardCodeTo) {
            filterValues += (filtersAdded ? "|" : "") + `cardCode:lte:${filter.cardCodeTo}`;
            filtersAdded = true;
        }

        if (filter.brand) {
            filterValues += (filtersAdded ? "|" : "") + `brand:like:${filter.brand}`;
            filtersAdded = true;
        }
        if (filter.model) {
            filterValues += (filtersAdded ? "|" : "") + `model:like:${filter.model}`;
            filtersAdded = true;
        }
        if (filter.color) {
            filterValues += (filtersAdded ? "|" : "") + `color:like:${filter.color}`;
            filtersAdded = true;
        }
        if (filter.year) {
            filterValues += (filtersAdded ? "|" : "") + `year:eq:${filter.year}`;
            filtersAdded = true;
        }

        filter.issDateFrom = filter.issDateFrom ? moment(filter.issDateFrom).format("YYYY-MM-DD") : undefined;
        filter.issDateTo = filter.issDateTo ? moment(filter.issDateTo).format("YYYY-MM-DD") : undefined;

        if (filter.issDateFrom && filter.issDateTo) {
            filterValues += (filtersAdded ? "|" : "") + `issDate:r:${filter.issDateFrom}:${filter.issDateTo}`;
            filtersAdded = true;
        } else if (filter.issDateFrom) {
            filterValues += (filtersAdded ? "|" : "") + `issDate:gte:${filter.issDateFrom}`;
            filtersAdded = true;
        } else if (filter.issDateTo) {
            filterValues += (filtersAdded ? "|" : "") + `issDate:lte:${filter.issDateTo}`;
            filtersAdded = true;
        }

        // Construir parámetros adicionales solo si tienen valor
        const extraParams = [];
        if (filter.guest) extraParams.push(`guest=${encodeURIComponent(filter.guest)}`);
        if (filter.unit) extraParams.push(`resident=${encodeURIComponent(filter.unit)}`);
        if (filter.residentsOnly !== undefined) extraParams.push(`residentsOnly=${filter.residentsOnly}`);

        // Concatenar parámetros extra con & si existen       
        const extraQuery = extraParams.length > 0 ? "&" + extraParams.join("&") : "";

        return this.http.get(fuseConfig.url + "/security/vehicleReport/" + building + filterValues + extraQuery);
    }

    getVehicleReportNew(building, filter): Observable<any> {

        let filterValues = `?filter=unit:like:${filter.unit}`;
        // console.log(filter);

        if (filter.guest) {
            filterValues += `|guest:like:${filter.guest}`;
        }

        if (filter.plate) {
            filterValues += `|plate:like:${filter.plate}`;
        }

        // if (filter.tag) {
        //     filterValues += `|tag:like:${filter.tag}`;
        // }

        if (filter.cardCodeFrom && filter.cardCodeTo) {
            filterValues += `|cardCode:r:${filter.cardCodeFrom}:${filter.cardCodeTo}`;
        } else if (filter.cardCodeFrom) {
            filterValues += `|cardCode:gte:${filter.cardCodeFrom}`;
        } else if (filter.cardCodeTo) {
            filterValues += `|cardCode:lte:${filter.cardCodeTo}`;
        }

        if (filter.brand) {
            filterValues += `|brand:like:${filter.brand}`;
        }
        if (filter.model) {
            filterValues += `|model:like:${filter.model}`;
        }
        if (filter.color) {
            filterValues += `|color:like:${filter.color}`;
        }
        if (filter.year) {
            filterValues += `|year:eq:${filter.year}`;
        }
        if (filter.cardFacility) {
            filterValues += `|cardFacility:eq:${filter.cardFacility}`;
        }
        if (filter.cardCode) {
            filterValues += `|cardCode:eq:${filter.cardCode}`;
        }
        if (filter.resident) {
            filterValues += `|residentUnit:like:${filter.resident}`;
        }
        if (filter.door) {
            filterValues += `|door:eq:${filter.door}`;
        }
        if (filter.action) {
            filterValues += `|cardCode:eq:${filter.action}`;
        }
        if (filter.guest_type) {
            filterValues += `|guestType:eq:${filter.guest_type}`;
        }

        const fromHour = moment(filter.fromHour, 'HH:mm');
        const toHour = moment(filter.toHour, 'HH:mm');
        filter.issDateFrom = filter.dateType != 'all' && filter.issDateFrom ? moment(filter.issDateFrom).set({ hour: fromHour.get('hour'), minute: fromHour.get('minute') }).format('YYYY-MM-DDTHH:mm:ss') : undefined;
        filter.issDateTo = filter.dateType != 'all' && filter.issDateTo ? moment(filter.issDateTo).set({ hour: toHour.get('hour'), minute: toHour.get('minute') }).format('YYYY-MM-DDTHH:mm:ss') : undefined;

        // if (filter.issDateFrom && filter.issDateTo) {
        //     filterValues += `|issDate:r:${filter.issDateFrom}:${filter.issDateTo}`;
        if (filter.issDateFrom) {
            filterValues += `|issDate:gte:${filter.issDateFrom}`;
        }
        if (filter.issDateTo) {
            filterValues += `|issDate:lte:${filter.issDateTo}`;
        }

        // if (filter.resident !== '') {
        //     filter += `|resident:in:${filter.resident}`;
        // }
        return this.http.get(fuseConfig.url + '/security/vehicleReport/' + building + filterValues + `&residentsOnly=${filter.residentsOnly}${filter.includeLogs ? '&includeLogs=true' : ''}`);
    }

    getGuestPhoto(guestId: string): Observable<any> {
        return this.http.get(`${fuseConfig.url}/security/guests/photo/license/${guestId}`);
    }

    postSecurityIncident(body): Observable<any> {
        return this.http.post(fuseConfig.url + `/security/${this.selectedBuilding}/incident`, body);
    }

    setupVideocallWebSocket(): any {
        const token = localStorage.getItem('token') ? localStorage.getItem('token') : sessionStorage.getItem('token');

        const wsClient = new faye.Client(`${fuseConfig.fayeUrl}/fayeWs`, {
            retry: 30
        });

        wsClient.addExtension({
            outgoing: (message: any, callback: any) => {
                message.ext = message.ext || {};
                message.ext.token = token;
                callback(message);
            }
        });

        return wsClient;
    }

    cancelVideoCall(kiosk: string, callId?: string): Observable<any> {
        return this.http.post(
            `${fuseConfig.url}/videocall/virtualGuard/cancel/${kiosk}`,
            callId ? { callId } : {}
        );
    }

    answerVideoCall(gate: string, callId?: string): Observable<any> {
        return this.http.post(
            `${fuseConfig.url}/videocall/virtualGuard/answered/${gate}`,
            callId ? { callId } : {}
        );
    }

    checkIn(): Observable<any> {
        return this.http.put(`${fuseConfig.url}/security/checkIn`, {});
    }

    checkOut(): Observable<any> {
        return this.http.put(`${fuseConfig.url}/security/checkOut`, {});
    }

    get authToken() {
        return localStorage.getItem("token") || sessionStorage.getItem("token");
    }

    get selectedBuilding() {
        return this._selectedBuilding;
    }

    // Al elegir el gate se piden ya las cookies de las fotos.
    set selectedBuilding(building) {
        this._selectedBuilding = building;
        this.cdnPhotoService.warmUp(building);
    }

    // Directo de CloudFront cuando se puede; si no, por el API como siempre.
    getImageUrl(img: string) {
        return this.cdnPhotoService.urlFor(img, this.selectedBuilding) || this.getApiImageUrl(img);
    }

    // Foto de un residente de getUnitResidents: CloudFront, o el base64 de siempre.
    getResidentImage(resident) {
        return this.cdnPhotoService.urlForPath(resident.img_url, this.selectedBuilding) ||
            (resident.img !== null ? 'data:image/png;base64,' + resident.img : resident.img);
    }

    // Foto del owner de getOwnerDetails por CloudFront; null si llego en base64.
    getOwnerImage(ownerDetails) {
        return this.cdnPhotoService.urlForPath(ownerDetails.owner_photo_url, this.selectedBuilding);
    }

    private getApiImageUrl(img: string) {
        return `${fuseConfig.url}/shared/image?img=${img}&X-GT-Authorization-Token=${this.authToken}`;
    }

    getImageFromUrl(url: string) {
        const image = this.http.get(url, { responseType: "blob" });
        // Esta descarga es la foto que se guarda con la entrada: si el CDN
        // falla se pide por el API antes de darla por perdida.
        const img = this.cdnPhotoService.legacyImgFor(url);
        if (!img) {
            return image;
        }
        return image.pipe(
            catchError(() => this.http.get(this.getApiImageUrl(img), { responseType: "blob" }))
        );
    }

    toggleFeatureMenuItems() {
        const menuFeaturesMap = {
            incident_report: this.selectedBuildingSettings.enableIncidentReport,
        };

        Object.keys(menuFeaturesMap).forEach((menuFeature) => {
            this._fuseNavigationService.updateNavigationItem(menuFeature, {
                hidden: !menuFeaturesMap[menuFeature],
            });
        });
    }

    postSecurityScanCheckout(body): Observable<any> {
        return this.http.post(fuseConfig.url + `/security/scanCheckout`, body);
    }

    postSecurityKioskSmartIdCamera(body): Observable<any> {
        return this.http.post(fuseConfig.url + `/security/kioskSmart/idcamera`, body);
    }

    getSecurityGateKioskToCall(gate): Observable<any> {
        return this.http.get(fuseConfig.url + `/security/gate/${gate}/kiosk-to-call`);
    }

    postVideocallVirtualGuardStartKioskCall(kiosk: string): Observable<any> {
        return this.http.post<any>(fuseConfig.url + `/videocall/virtualGuard/startKioskCall/${kiosk}`,{});
    }

    getSecuritySpeedLimitWarning(building, carPlate): Observable<any> {
        return this.http.get(fuseConfig.url + `/security/speed-limit-warning/${building}?carPlate=${carPlate}`);
    }

    putVehicleNotify(building, body): Observable<any> {
        return this.http.put(fuseConfig.url + `/building/${building}/vehicle-notify`, body);
    }

}